/**
 * Resizing the invitation art in the browser.
 *
 * A photo straight off a phone is several megabytes and the wrong shape. Doing
 * this here rather than in Lambda means no image library on the server, no
 * cold-start cost, and no multi-megabyte upload through the API.
 *
 * Two images come out of one file:
 *
 *  - `invite`, the picture the guest sees, kept in its own proportions
 *  - `og`, cropped to 1200x630 for the WhatsApp preview card, because that
 *    card is a fixed shape and anything else gets cropped by WhatsApp instead,
 *    usually badly
 */

export const OG_WIDTH = 1200;
export const OG_HEIGHT = 630;

/** WhatsApp silently ignores a preview image over 600KB. */
const OG_MAX_BYTES = 600 * 1024;
const INVITE_MAX_BYTES = 2 * 1024 * 1024;
const INVITE_MAX_EDGE = 1400;

export interface PreparedArt {
  invite: Blob;
  og: Blob;
  previewUrl: string;
}

/** Takes a Blob rather than a File, so generated art goes through this too. */
export async function prepareArt(file: Blob): Promise<PreparedArt> {
  const bitmap = await loadBitmap(file);

  try {
    const invite = await encodeUnder(
      drawContained(bitmap, INVITE_MAX_EDGE),
      INVITE_MAX_BYTES,
    );
    const og = await encodeUnder(drawOgCrop(bitmap), OG_MAX_BYTES);

    return { invite, og, previewUrl: URL.createObjectURL(invite) };
  } finally {
    bitmap.close?.();
  }
}

async function loadBitmap(file: Blob): Promise<ImageBitmap> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Escolha uma imagem.');
  }
  try {
    return await createImageBitmap(file);
  } catch {
    throw new Error('Não conseguimos abrir essa imagem.');
  }
}

/** Fit inside a square of `maxEdge`, never enlarging a small image. */
function drawContained(bitmap: ImageBitmap, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d')!;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, width, height);
  return canvas;
}

/** Cover-crop to exactly 1200x630, centred. */
function drawOgCrop(bitmap: ImageBitmap): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = OG_WIDTH;
  canvas.height = OG_HEIGHT;

  const context = canvas.getContext('2d')!;
  // A white bed rather than transparent: JPEG has no alpha, and an unpainted
  // canvas encodes as black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, OG_WIDTH, OG_HEIGHT);
  context.imageSmoothingQuality = 'high';

  const scale = Math.max(OG_WIDTH / bitmap.width, OG_HEIGHT / bitmap.height);
  const width = bitmap.width * scale;
  const height = bitmap.height * scale;

  context.drawImage(
    bitmap,
    (OG_WIDTH - width) / 2,
    (OG_HEIGHT - height) / 2,
    width,
    height,
  );
  return canvas;
}

/**
 * Encode as JPEG, dropping quality until it fits.
 *
 * Stepping down beats guessing a quality: the same setting can produce 200KB
 * from a flat illustration and 900KB from a photograph.
 */
async function encodeUnder(
  canvas: HTMLCanvasElement,
  maxBytes: number,
): Promise<Blob> {
  let blob: Blob | null = null;

  for (const quality of [0.85, 0.75, 0.65, 0.55, 0.45, 0.35]) {
    blob = await toBlob(canvas, quality);
    if (blob.size <= maxBytes) return blob;
  }

  if (blob && blob.size > maxBytes) {
    throw new Error('Não conseguimos deixar essa imagem leve o bastante.');
  }
  return blob!;
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encode falhou'))),
      'image/jpeg',
      quality,
    );
  });
}

/** PUT each blob straight to S3 with its presigned URL. */
export async function uploadArt(
  uploads: { kind: string; url: string }[],
  prepared: PreparedArt,
): Promise<void> {
  await Promise.all(
    uploads.map(async ({ kind, url }) => {
      const body = kind === 'og' ? prepared.og : prepared.invite;
      const response = await fetch(url, {
        method: 'PUT',
        body,
        headers: { 'content-type': 'image/jpeg' },
      });
      if (!response.ok) {
        throw new Error('Não conseguimos enviar a imagem.');
      }
    }),
  );
}
